/**
 * Data Contract Service
 * Handles CRUD operations for data contracts, field requirements, and submissions
 *
 * Features:
 * - Contract lifecycle management (draft -> active -> archived)
 * - Field requirement configuration
 * - Public URL and QR code generation
 * - Contract duplication and versioning
 * - Integration with division-based permissions
 */

import { supabase, handleSupabaseArray } from '@xanectech/supabase-client';
import type { Json } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import { customAlphabet } from 'nanoid';
import type {
  Database,
  LineageMetadata,
  DataContract,
  DataContractWithSubmissions,
  ContractLineageNode,
  ContractFieldRequirement,
  CreateContractRequest,
  UpdateContractRequest,
  CreateFieldRequirementRequest,
  DataContractWithRequirements,
  UserContractStats,
} from '@xanectech/types';

const logger = createLogger('Data - DataContractService');
const slugSuffix = customAlphabet('abcdefghijklmnopqrstuvwxyz0123456789', 6);

function getSubmitBaseUrl(): string {
  const importMetaEnv = (
    import.meta as unknown as { env?: Record<string, string | undefined> }
  ).env;

  return (
    importMetaEnv?.VITE_SUBMIT_URL || `${window.location.origin}/submit`
  ).replace(/\/+$/, '');
}

interface StatusRow {
  status: string | null;
}

type DataContractRow = Database['public']['Tables']['data_contracts']['Row'];
type DataContractInsert =
  Database['public']['Tables']['data_contracts']['Insert'];
type DataContractUpdate =
  Database['public']['Tables']['data_contracts']['Update'];
type ContractFieldRequirementRow =
  Database['public']['Tables']['contract_field_requirements']['Row'];

function optionalString(value: string | null): string | undefined {
  return value ?? undefined;
}

function optionalTiptapContent(
  value: Json | null,
): DataContract['terms_conditions_json'] {
  if (value === null) {
    return null;
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as DataContract['terms_conditions_json'];
  }

  return undefined;
}

function requiredString(value: string | null, fieldName: string): string {
  if (value === null) {
    throw new Error(`Invalid database row: missing ${fieldName}`);
  }
  return value;
}

function toContractStatus(value: string): DataContract['status'] {
  if (
    value === 'draft' ||
    value === 'active' ||
    value === 'inactive' ||
    value === 'archived'
  ) {
    return value;
  }
  return 'draft';
}

function toSignatureType(value: string): DataContract['signature_type'] {
  if (value === 'digital' || value === 'physical' || value === 'both') {
    return value;
  }
  return 'digital';
}

function toRequirementLogic(
  value: string,
): ContractFieldRequirement['requirement_logic'] {
  if (value === 'all' || value === 'any' || value === 'exact') {
    return value;
  }
  return 'all';
}

function toLineageMetadata(value: Json): LineageMetadata | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const raw = value as Record<string, Json>;
  const rawGeneration = raw.generation;
  const generation = typeof rawGeneration === 'number' ? rawGeneration : 1;

  const parentContractId =
    typeof raw.parent_contract_id === 'string'
      ? raw.parent_contract_id
      : undefined;

  const createdReason =
    typeof raw.created_reason === 'string' ? raw.created_reason : undefined;

  const childrenIds = Array.isArray(raw.children_ids)
    ? raw.children_ids.filter(
        (childId): childId is string => typeof childId === 'string',
      )
    : undefined;

  return {
    generation,
    parent_contract_id: parentContractId,
    created_reason: createdReason,
    children_ids: childrenIds,
  };
}

function toDataContract(row: DataContractRow): DataContract {
  return {
    id: row.id,
    company_id: row.company_id,
    division_id: row.division_id,
    name: row.name,
    description: optionalString(row.description),
    purpose: row.purpose,
    legal_basis: optionalString(row.legal_basis),
    retention_period: optionalString(row.retention_period),
    signature_type: toSignatureType(row.signature_type),
    terms_conditions: optionalString(row.terms_conditions),
    terms_conditions_json: optionalTiptapContent(row.terms_conditions_json),
    template_path: optionalString(row.template_path),
    template_content: optionalString(row.template_content),
    status: toContractStatus(row.status),
    qr_code_data: optionalString(row.qr_code_data),
    public_url_slug: optionalString(row.public_url_slug),
    version: row.version ?? 1,
    expires_at: optionalString(row.expires_at),
    created_by: optionalString(row.created_by),
    created_at: requiredString(row.created_at, 'data_contracts.created_at'),
    updated_at: requiredString(row.updated_at, 'data_contracts.updated_at'),
    lineage_metadata: toLineageMetadata(row.lineage_metadata),
  };
}

function toContractFieldRequirement(
  row: ContractFieldRequirementRow,
): ContractFieldRequirement {
  return {
    id: row.id,
    contract_id: row.contract_id,
    category_id: optionalString(row.category_id),
    field_id: optionalString(row.field_id),
    is_required: row.is_required ?? false,
    entry_type: optionalString(row.entry_type),
    requirement_logic: toRequirementLogic(row.requirement_logic),
    minimum_entries: row.minimum_entries ?? 1,
    maximum_entries: row.maximum_entries ?? undefined,
    placeholder_name: optionalString(row.placeholder_name),
    display_order: row.display_order ?? 0,
    created_at: requiredString(
      row.created_at,
      'contract_field_requirements.created_at',
    ),
  };
}

/**
 * Data Contract Service Class
 */
export class DataContractService {
  // =================== CONTRACT MANAGEMENT ===================

  /**
   * Create a new data contract
   */
  static async createContract(
    contract: CreateContractRequest,
  ): Promise<DataContract> {
    // Get current user for created_by
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      throw new Error('Authentication required to create contract');
    }

    // Generate unique public URL slug
    const baseSlug = contract.name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
      .substring(0, 50);

    let publicUrlSlug = baseSlug;
    let counter = 1;

    // Ensure slug uniqueness
    while (true) {
      const { data: existingContract } = await supabase
        .from('data_contracts')
        .select('id')
        .eq('public_url_slug', publicUrlSlug)
        .single();

      if (!existingContract) break;

      publicUrlSlug = `${baseSlug}-${counter}`;
      counter++;
    }

    const contractPayload: DataContractInsert = {
      ...contract,
      terms_conditions_json:
        (contract.terms_conditions_json as Json | null | undefined) ?? null,
      public_url_slug: publicUrlSlug,
      created_by: user.id,
      signature_type: contract.signature_type || 'digital',
    };

    const { data, error } = await supabase
      .from('data_contracts')
      .insert(contractPayload)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned when creating contract');
    return toDataContract(data);
  }

  /**
   * Get all contracts for a specific division
   */
  static async getContractsByDivision(
    divisionId: string,
  ): Promise<DataContract[]> {
    const { data, error } = await supabase
      .from('data_contracts')
      .select('*')
      .eq('division_id', divisionId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return handleSupabaseArray<DataContractRow>(data).map(toDataContract);
  }

  /**
   * Get all contracts for a company
   */
  static async getContractsByCompany(
    companyId: string,
  ): Promise<DataContract[]> {
    const { data, error } = await supabase
      .from('data_contracts')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return handleSupabaseArray<DataContractRow>(data).map(toDataContract);
  }

  /**
   * Get active contracts for a division
   */
  static async getActiveContractsByDivision(
    divisionId: string,
  ): Promise<DataContract[]> {
    const { data, error } = await supabase
      .from('data_contracts')
      .select('*')
      .eq('division_id', divisionId)
      .eq('status', 'active')
      .order('name');

    if (error) throw error;
    return handleSupabaseArray<DataContractRow>(data).map(toDataContract);
  }

  /**
   * Get a single contract by ID
   */
  static async getContract(contractId: string): Promise<DataContract | null> {
    const { data, error } = await supabase
      .from('data_contracts')
      .select('*')
      .eq('id', contractId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw error;
    }
    if (!data) return null;
    return toDataContract(data);
  }

  /**
   * Get contract by public URL slug
   */
  static async getContractBySlug(slug: string): Promise<DataContract | null> {
    const { data, error } = await supabase
      .from('data_contracts')
      .select('*')
      .eq('public_url_slug', slug)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw error;
    }
    if (!data) return null;
    return toDataContract(data);
  }

  /**
   * Update an existing contract
   * Note: Published contracts (active/inactive) cannot have content modified, only status changes allowed
   */
  static async updateContract(
    contractId: string,
    updates: UpdateContractRequest,
  ): Promise<DataContract> {
    // Check if contract is published and trying to update content (not just status)
    const { data: contract } = await supabase
      .from('data_contracts')
      .select('status')
      .eq('id', contractId)
      .single();

    if (contract && contract.status !== 'draft') {
      // Published contracts only allow status transitions and sharing metadata updates.
      const allowedPublishedFields = new Set([
        'status',
        'public_url_slug',
        'qr_code_data',
      ]);
      const isAllowedPublishedUpdate = Object.keys(updates).every((key) =>
        allowedPublishedFields.has(key),
      );

      if (!isAllowedPublishedUpdate) {
        throw new Error(
          'Cannot modify published contracts. Please duplicate this contract to make changes.',
        );
      }
    }

    const updatePayload: DataContractUpdate = {
      ...updates,
      terms_conditions_json:
        (updates.terms_conditions_json as Json | null | undefined) ?? null,
    };

    const { data, error } = await supabase
      .from('data_contracts')
      .update(updatePayload)
      .eq('id', contractId)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned when updating contract');
    return toDataContract(data);
  }

  /**
   * Duplicate a contract with a new name
   * Enhanced to support auto-incrementing names, lineage tracking, and copying all relevant fields
   */
  static async duplicateContract(
    contractId: string,
    newName?: string,
    createdReason?: string,
  ): Promise<DataContract> {
    // Get the original contract
    const originalContract = await this.getContract(contractId);
    if (!originalContract) {
      throw new Error('Contract not found');
    }

    // Calculate generation (parent's generation + 1)
    const parentGeneration = originalContract.lineage_metadata?.generation || 1;
    const newGeneration = parentGeneration + 1;

    // Auto-generate name if not provided
    let finalName = newName;
    if (!finalName) {
      // Try incrementing pattern: "Name (Copy)", "Name (Copy 2)", etc.
      const baseName = originalContract.name;
      finalName = `${baseName} (Copy)`;

      let counter = 2;
      while (true) {
        const { data: existingContract } = await supabase
          .from('data_contracts')
          .select('id')
          .eq('name', finalName)
          .eq('company_id', originalContract.company_id)
          .single();

        if (!existingContract) break;

        finalName = `${baseName} (Copy ${counter})`;
        counter++;
      }
    }

    // Prepare lineage metadata
    const lineageMetadata: LineageMetadata = {
      parent_contract_id: contractId,
      generation: newGeneration,
      created_reason: createdReason,
    };

    // Create new contract with all relevant fields copied
    const duplicatedContract = await this.createContract({
      company_id: originalContract.company_id,
      division_id: originalContract.division_id,
      name: finalName,
      description: originalContract.description,
      purpose: originalContract.purpose,
      legal_basis: originalContract.legal_basis,
      retention_period: originalContract.retention_period,
      signature_type: originalContract.signature_type,
      terms_conditions: originalContract.terms_conditions,
      terms_conditions_json: originalContract.terms_conditions_json,
      template_content: originalContract.template_content,
      expires_at: originalContract.expires_at,
    });

    // Update the duplicated contract with lineage metadata
    await this.updateContractLineage(duplicatedContract.id, lineageMetadata);

    // Update parent's children_ids list
    await this.addChildToParent(contractId, duplicatedContract.id);

    // Copy field requirements
    const originalRequirements =
      await this.getContractFieldRequirements(contractId);
    if (originalRequirements.length > 0) {
      const newRequirements = originalRequirements.map((req) => ({
        contract_id: duplicatedContract.id,
        category_id: req.category_id,
        field_id: req.field_id,
        is_required: req.is_required,
        entry_type: req.entry_type,
        requirement_logic: req.requirement_logic,
        minimum_entries: req.minimum_entries,
        maximum_entries: req.maximum_entries,
        placeholder_name: req.placeholder_name,
        display_order: req.display_order,
      }));

      await this.bulkCreateFieldRequirements(newRequirements);
    }

    // Refetch to get updated lineage metadata
    const result = await this.getContract(duplicatedContract.id);
    if (!result) throw new Error('Failed to fetch duplicated contract');
    return result;
  }

  /**
   * Activate a contract (change status to active)
   */
  static async activateContract(contractId: string): Promise<void> {
    await this.updateContract(contractId, { status: 'active' });
  }

  /**
   * Deactivate a contract (change status to inactive)
   */
  static async deactivateContract(contractId: string): Promise<void> {
    await this.updateContract(contractId, { status: 'inactive' });
  }

  /**
   * Archive a contract (change status to archived)
   */
  static async archiveContract(contractId: string): Promise<void> {
    await this.updateContract(contractId, { status: 'archived' });
  }

  /**
   * Delete a contract and all related data
   */
  static async deleteContract(contractId: string): Promise<void> {
    const { error } = await supabase
      .from('data_contracts')
      .delete()
      .eq('id', contractId);

    if (error) throw error;
  }

  // =================== FIELD REQUIREMENTS ===================

  /**
   * Get field requirements for a contract
   */
  static async getContractFieldRequirements(
    contractId: string,
  ): Promise<ContractFieldRequirement[]> {
    const { data, error } = await supabase
      .from('contract_field_requirements')
      .select('*')
      .eq('contract_id', contractId)
      .order('display_order');

    if (error) throw error;
    return handleSupabaseArray<ContractFieldRequirementRow>(data).map(
      toContractFieldRequirement,
    );
  }

  /**
   * Add a field requirement to a contract
   */
  static async createFieldRequirement(
    requirement: CreateFieldRequirementRequest,
  ): Promise<ContractFieldRequirement> {
    const { data, error } = await supabase
      .from('contract_field_requirements')
      .insert({
        ...requirement,
        is_required: requirement.is_required ?? false,
        requirement_logic: requirement.requirement_logic || 'all',
        minimum_entries: requirement.minimum_entries ?? 1,
        display_order: requirement.display_order ?? 0,
      })
      .select()
      .single();

    if (error) throw error;
    if (!data)
      throw new Error('No data returned when creating field requirement');
    return toContractFieldRequirement(data);
  }

  /**
   * Bulk create field requirements
   */
  static async bulkCreateFieldRequirements(
    requirements: CreateFieldRequirementRequest[],
  ): Promise<ContractFieldRequirement[]> {
    if (requirements.length === 0) return [];

    const { data, error } = await supabase
      .from('contract_field_requirements')
      .insert(
        requirements.map((req) => ({
          ...req,
          is_required: req.is_required ?? false,
          requirement_logic: req.requirement_logic || 'all',
          minimum_entries: req.minimum_entries ?? 1,
          display_order: req.display_order ?? 0,
        })),
      )
      .select();

    if (error) throw error;
    return handleSupabaseArray<ContractFieldRequirementRow>(data).map(
      toContractFieldRequirement,
    );
  }

  /**
   * Update a field requirement
   */
  static async updateFieldRequirement(
    requirementId: string,
    updates: Partial<CreateFieldRequirementRequest>,
  ): Promise<ContractFieldRequirement> {
    const { data, error } = await supabase
      .from('contract_field_requirements')
      .update(updates)
      .eq('id', requirementId)
      .select()
      .single();

    if (error) throw error;
    if (!data)
      throw new Error('No data returned when updating field requirement');
    return toContractFieldRequirement(data);
  }

  /**
   * Delete a field requirement
   */
  static async deleteFieldRequirement(requirementId: string): Promise<void> {
    const { error } = await supabase
      .from('contract_field_requirements')
      .delete()
      .eq('id', requirementId);

    if (error) throw error;
  }

  /**
   * Update display order for field requirements
   */
  static async updateFieldRequirementOrder(
    contractId: string,
    orderedRequirementIds: string[],
  ): Promise<void> {
    const updates = orderedRequirementIds.map((id, index) => ({
      id,
      display_order: index + 1,
    }));

    for (const update of updates) {
      await this.updateFieldRequirement(update.id, {
        display_order: update.display_order,
      });
    }
  }

  // =================== CONTRACT WITH REQUIREMENTS ===================

  /**
   * Get contract with all field requirements
   */
  static async getContractWithRequirements(
    contractId: string,
  ): Promise<DataContractWithRequirements | null> {
    const contract = await this.getContract(contractId);
    if (!contract) return null;

    const fieldRequirements =
      await this.getContractFieldRequirements(contractId);

    return {
      ...contract,
      field_requirements: fieldRequirements,
    };
  }

  // =================== PUBLIC URL AND QR CODE ===================

  /**
   * Generate public URL for a contract
   */
  static async generatePublicUrl(contractId: string): Promise<string> {
    const contract = await this.getContract(contractId);
    if (!contract) {
      throw new Error('Contract not found');
    }

    // Return the existing public URL if it exists
    if (contract.public_url_slug) {
      return `${getSubmitBaseUrl()}/${contract.public_url_slug}`;
    }

    // This shouldn't happen as URL is generated on creation, but handle gracefully
    throw new Error('Contract does not have a public URL slug');
  }

  /**
   * Generate QR code data for a contract
   */
  static async generateQRCode(contractId: string): Promise<string> {
    const publicUrl = await this.generatePublicUrl(contractId);

    // For now, return the URL - in a real implementation, you'd generate SVG/PNG QR code
    // You can integrate with libraries like 'qrcode' or 'qr-server' later
    const qrCodeData = JSON.stringify({
      url: publicUrl,
      contract_id: contractId,
      generated_at: new Date().toISOString(),
    });

    // Update the contract with QR code data
    await this.updateContract(contractId, { qr_code_data: qrCodeData });

    return qrCodeData;
  }

  /**
   * Regenerate public URL slug for a contract
   */
  static async regeneratePublicUrl(contractId: string): Promise<string> {
    const contract = await this.getContract(contractId);
    if (!contract) {
      throw new Error('Contract not found');
    }

    // Generate new slug with random suffix
    const baseSlug = contract.name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
      .substring(0, 40);

    const fallbackSlugPrefix =
      baseSlug.length > 0 ? baseSlug : `contract-${contractId.substring(0, 8)}`;

    let newSlug = `${fallbackSlugPrefix}-${slugSuffix()}`;

    // Ensure the regenerated slug does not conflict with another contract.
    while (true) {
      const { data: existingContract, error } = await supabase
        .from('data_contracts')
        .select('id')
        .eq('public_url_slug', newSlug)
        .single();

      if (error?.code === 'PGRST116' || !existingContract) {
        break;
      }

      if (error) {
        throw error;
      }

      if (existingContract.id === contractId) {
        break;
      }

      newSlug = `${fallbackSlugPrefix}-${slugSuffix()}`;
    }

    const publicUrl = `${getSubmitBaseUrl()}/${newSlug}`;
    const qrCodeData = JSON.stringify({
      url: publicUrl,
      contract_id: contractId,
      generated_at: new Date().toISOString(),
    });

    await this.updateContract(contractId, {
      public_url_slug: newSlug,
      qr_code_data: qrCodeData,
    });

    return publicUrl;
  }

  // =================== STATISTICS AND ANALYTICS ===================

  /**
   * Get contract statistics for a division
   */
  static async getDivisionContractStats(divisionId: string) {
    const { data, error } = await supabase
      .from('data_contracts')
      .select('status')
      .eq('division_id', divisionId);

    if (error) throw error;

    const contracts = handleSupabaseArray<StatusRow>(data);

    return {
      total: contracts.length,
      draft: contracts.filter((contract) => contract.status === 'draft').length,
      active: contracts.filter((contract) => contract.status === 'active')
        .length,
      inactive: contracts.filter((contract) => contract.status === 'inactive')
        .length,
      archived: contracts.filter((contract) => contract.status === 'archived')
        .length,
    };
  }

  /**
   * Get contract statistics for a company
   */
  static async getCompanyContractStats(companyId: string) {
    const { data, error } = await supabase
      .from('data_contracts')
      .select('status')
      .eq('company_id', companyId);

    if (error) throw error;

    const contracts = handleSupabaseArray<StatusRow>(data);

    return {
      total: contracts.length,
      draft: contracts.filter((contract) => contract.status === 'draft').length,
      active: contracts.filter((contract) => contract.status === 'active')
        .length,
      inactive: contracts.filter((contract) => contract.status === 'inactive')
        .length,
      archived: contracts.filter((contract) => contract.status === 'archived')
        .length,
    };
  }

  /**
   * Get user-specific contract statistics
   */
  static async getUserContractStats(
    userId: string,
  ): Promise<UserContractStats> {
    const { data, error } = await supabase
      .from('contract_submissions')
      .select('status')
      .eq('user_id', userId);

    if (error) throw error;

    const contracts = handleSupabaseArray<StatusRow>(data);

    return {
      total_contracts: contracts.length,
      submitted_contracts: contracts.filter(
        (contract) => contract.status !== 'draft',
      ).length,
      active_contracts: contracts.filter(
        (contract) => contract.status === 'active',
      ).length,
      draft_contracts: contracts.filter(
        (contract) => contract.status === 'draft',
      ).length,
      contract_invitations: 0, // TODO: Replace with actual invitations count if available
    };
  }

  // =================== LINEAGE TRACKING ===================

  /**
   * Update contract lineage metadata
   */
  static async updateContractLineage(
    contractId: string,
    lineageMetadata: LineageMetadata,
  ): Promise<void> {
    const { error } = await supabase
      .from('data_contracts')
      .update({ lineage_metadata: lineageMetadata as unknown as Json })
      .eq('id', contractId);

    if (error) throw error;
  }

  /**
   * Add child contract ID to parent's children list
   */
  static async addChildToParent(
    parentId: string,
    childId: string,
  ): Promise<void> {
    const parent = await this.getContract(parentId);
    if (!parent) return;

    const currentMetadata = parent.lineage_metadata || { generation: 1 };
    const childrenIds = currentMetadata.children_ids || [];

    if (!childrenIds.includes(childId)) {
      childrenIds.push(childId);
    }

    await this.updateContractLineage(parentId, {
      ...currentMetadata,
      children_ids: childrenIds,
    });
  }

  /**
   * Get contract lineage (all ancestors)
   * Returns contracts from root to current contract
   */
  static async getContractLineage(contractId: string): Promise<DataContract[]> {
    const lineage: DataContract[] = [];
    let currentId: string | undefined = contractId;

    // Traverse up the parent chain
    while (currentId) {
      const contract = await this.getContract(currentId);
      if (!contract) break;

      lineage.unshift(contract); // Add to beginning (root first)
      currentId = contract.lineage_metadata?.parent_contract_id;
    }

    return lineage;
  }

  /**
   * Get contract family tree starting from root
   * Returns hierarchical structure with all descendants
   */
  static async getContractFamilyTree(
    contractId: string,
  ): Promise<ContractLineageNode> {
    const contract = await this.getContract(contractId);
    if (!contract) {
      throw new Error('Contract not found');
    }

    // Get submission count for this contract
    const { data: submissions } = await supabase
      .from('contract_submissions')
      .select('id')
      .eq('contract_id', contractId);

    const submissionCount = submissions?.length || 0;

    // Get children
    const childrenIds = contract.lineage_metadata?.children_ids || [];
    const children: ContractLineageNode[] = [];

    for (const childId of childrenIds) {
      try {
        const childNode = await this.getContractFamilyTree(childId);
        children.push(childNode);
      } catch (error) {
        // Skip if child not found
        logger.warn(`Child contract ${childId} not found`);
      }
    }

    return {
      contract,
      children,
      submission_count: submissionCount,
    };
  }

  /**
   * Get all versions in a contract family (flattened)
   */
  static async getContractVersions(
    contractId: string,
  ): Promise<DataContractWithSubmissions[]> {
    // Find root contract
    const lineage = await this.getContractLineage(contractId);
    const rootContract = lineage[0];

    if (!rootContract) {
      throw new Error('Root contract not found');
    }

    // Get entire family tree
    const familyTree = await this.getContractFamilyTree(rootContract.id);

    // Flatten tree to array
    const versions: DataContractWithSubmissions[] = [];

    const flattenTree = (node: ContractLineageNode) => {
      versions.push({
        ...node.contract,
        submission_count: node.submission_count,
      });
      node.children.forEach(flattenTree);
    };

    flattenTree(familyTree);
    return versions;
  }

  /**
   * Get submission counts by contract family
   * Returns aggregated stats for entire contract lineage
   */
  static async getSubmissionCountsByFamily(contractId: string): Promise<{
    total: number;
    by_version: Array<{
      contract_id: string;
      name: string;
      count: number;
      status: string;
    }>;
  }> {
    const versions = await this.getContractVersions(contractId);

    const byVersion = versions.map((v) => ({
      contract_id: v.id,
      name: v.name,
      count: v.submission_count,
      status: v.status,
    }));

    const total = byVersion.reduce((sum, v) => sum + v.count, 0);

    return {
      total,
      by_version: byVersion,
    };
  }

  /**
   * Get root contract of a lineage
   */
  static async getRootContract(contractId: string): Promise<DataContract> {
    const lineage = await this.getContractLineage(contractId);
    const rootContract = lineage[0];

    if (!rootContract) {
      throw new Error('Root contract not found in lineage');
    }

    return rootContract;
  }

  /**
   * Check if contract has descendants
   */
  static async hasDescendants(contractId: string): Promise<boolean> {
    const contract = await this.getContract(contractId);
    const childrenIds = contract?.lineage_metadata?.children_ids || [];
    return childrenIds.length > 0;
  }

  // =================== VALIDATION HELPERS ===================

  /**
   * Validate contract data before creation/update
   */
  static validateContractData(
    data: CreateContractRequest | UpdateContractRequest,
  ): void {
    if ('name' in data && data.name && data.name.length > 255) {
      throw new Error('Contract name must be 255 characters or less');
    }

    if ('purpose' in data && data.purpose && data.purpose.length < 10) {
      throw new Error(
        'Purpose must be at least 10 characters for GDPR compliance',
      );
    }

    if ('expires_at' in data && data.expires_at) {
      const expiryDate = new Date(data.expires_at);
      if (expiryDate <= new Date()) {
        throw new Error('Expiry date must be in the future');
      }
    }
  }
}
